import { api } from "@/lib/api";
import axios from "axios";
import {
  type OperatorIdentity,
  unwrapCurrentOperator,
} from "@/services/auth0/session";

export function validateOperatorName(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return "Name is required";
  if (trimmed.length > 120) return "Name must be 120 characters or fewer";
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    return "Enter your name, not an email address";
  }
  return null;
}

export function operatorNameError(error: unknown): string {
  const axiosError = axios.isAxiosError<unknown>(error) ? error : null;
  const status = axiosError?.response?.status;
  const data = axiosError?.response?.data;
  const serverMessage =
    data && typeof data === "object" && "error" in data
      ? Reflect.get(data, "error")
      : undefined;

  if (status === 400) {
    return typeof serverMessage === "string"
      ? serverMessage
      : "Enter a valid name";
  }
  if (status === 401 || status === 403) {
    return "Your session cannot update this profile. Sign in again or contact dispatch.";
  }
  if (
    axiosError?.code === "ERR_NETWORK" ||
    (error instanceof Error && /network/i.test(error.message))
  ) {
    return "You're offline. Reconnect and save again.";
  }
  return "We couldn't update your name. Please try again.";
}

export async function updateCurrentOperatorName(
  name: string
): Promise<OperatorIdentity> {
  const validationError = validateOperatorName(name);
  if (validationError) throw new Error(validationError);

  const response = await api.patch("/api/v2/operator/me", {
    name: name.trim(),
  });
  const operator = unwrapCurrentOperator(response.data);
  if (!operator) {
    throw new Error("Updated operator response did not include an operator");
  }
  return operator;
}
